export interface ConfirmDepositInvoiceUploadResponse {
  deposit_invoice_id: string;
  success: boolean;
}

export interface DepositInvoicePresignedUrlRequest {
  transaction_id: string;
  file_type: 'pdf' | 'png' | 'jpg' | 'jpeg' | 'heic';
}

export interface DepositInvoicePresignedUrlResponse {
  presigned_url: string;
  s3_key: string;
  expires_in_seconds: number;
  max_file_size_bytes: number;
}

export interface ConfirmDepositInvoiceUploadRequest {
  transaction_id: string;
  file_size_bytes: number;
}

export enum CashAdvanceOptionsStatus {
  Active = 'active',
  Selected = 'selected',
  Declined = 'declined',
  Expired = 'expired',
  None = 'none',
}

export interface PresentedLoanOption {
  terms_id: string;
  funding_source?: string;
  term_in_months?: number;
  monthly_payment?: number;
  amount?: number;
  fees_amount?: number;
  cash_to_customer?: number;
  presented?: boolean;
  selected?: boolean;
}

export interface CashAdvanceOptionsResponse {
  status: CashAdvanceOptionsStatus;
  options_expire_at?: string | null;
  options_presented_at?: string | null;
  options_selected_at?: string | null;
  options_declined_at?: string | null;
  options_declined_reason?: string | null;
  options_expired_at?: string | null;
  presentation_attempt?: number;
  options: PresentedLoanOption[];
}

export interface DisplayOption {
  terms_id: string;
  term_in_months: number;
  monthly_payment_dollars: number;
  total_cost_dollars: number;
}

// ── Deposit into TACH (customer-initiated ACH debit pull) ────────────────────
// NOTE: the wire types (DepositFundsRequest, DepositFundsAuthorization, PendingTransactionItem,
// LinkedAccountDepositEligibility) + DEPOSIT_LIMITS_IN_CENTS come from the backend via generated
// `types/apiTypes.ts` — do NOT re-declare them here. Only genuinely FE-only types live below.

// `no_auth_accounts` is only surfaced at deposit-submit time (Plaid /auth/get finds no checking or
// savings account on the item); the account-list `deposit_ineligible_reason` never produces it.
export type DepositIneligibleReason =
  | 'not_linked'
  | 'debit_disabled'
  | 'no_auth_accounts';

// Outcome of a deposit submit, mapped from the backend status + body so the modal can branch. This
// is a FE-only concept (no backend equivalent). Expected business failures are RETURNED (not thrown);
// only unexpected failures (network/500) throw.
export type DepositOutcome =
  | { status: 'success' }
  | { status: 'limit_exceeded'; message?: string } // per-txn or daily/monthly cap (429 + message)
  | { status: 'resource_locked' } // another deposit in flight — try again shortly (429, no message)
  | { status: 'deposits_disabled'; message?: string } // kill switch off (503)
  | {
      status: 'account_not_debit_capable';
      reason: DepositIneligibleReason;
      message?: string;
    } // 422
  | { status: 'insufficient_balance'; message?: string } // not enough in the source account (422)
  | { status: 'reauth_required' }; // Plaid item stale — re-link then retry (409)
